import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { EditorTabs } from '@/components/EditorTabs';
import { SearchSharingCard } from '@/components/SearchSharingCard';
import { MarkdownField } from '@/components/MarkdownField';

import { languageTabs, saveMessage } from '@/lib/apps';
import { useEditorBar } from '@/lib/editor-bar';
import { previewUrl, useWebsiteUrl } from '@/lib/share-image';
import {
  postDate,
  statusOf,
  useAdminPost,
  usePostCover,
  usePostShareImage,
  useSavePost,
  wordCount,
  type AdminPost,
  type PostContentInput,
  type UpdatePostInput,
} from '@/lib/posts';
import { bylineOptions, useTeamMembers } from '@/lib/team';
import { topicName, useAdminTopics } from '@/lib/topics';
import { localeLabels, type Locale } from '@sonasi/shared-types';
import { useSeeded } from '@/lib/seeded';

/**
 * Writing a post.
 *
 * The same shape as the app editor and for the same reasons: language tabs with
 * a percentage, the save bar published to the shell's sticky header, and two
 * columns at `1 1 480px` against `0 1 340px` so the writing gets the slack and
 * the side panel keeps its size.
 *
 * ⚠️ **Three things the design draws that this screen does not save**, and all
 * three are now decisions rather than omissions — none is waiting on anything.
 * Written out because the mock-up still draws all three, and somebody building
 * from it again would put them back.
 *
 * - **Tags.** [Blog 7] — **dropped**, on the studio's call. The design draws
 *   Topic *and* Tags, but nothing outside this mock-up ever used the second:
 *   the design's own posts carry a single `tag`, which is what [Blog 3] turned
 *   into `topics`; the public blog's filter chips are those topics; the post
 *   page prints one classification. A `tags` table would have been a second
 *   vocabulary no page reads and no content fills — which is how two places to
 *   type the same word begin. `topicId` is the one classification a post has.
 * - **Related app.** [Blog 8] — **dropped**, same call and the same evidence:
 *   the design's posts have no app field, the public post page has no block for
 *   one, and `BLOG_UI` has no sentence to put in it. The other direction — a
 *   list of posts on an app's page — was never drawn at all.
 * - **Meta title and meta description.** [Blog 5] — **dropped**, and this one
 *   was a decision rather than an omission. `title` and `excerpt` are what the
 *   post's page publishes as its `<title>` and `description`, so a second pair
 *   of boxes would be two places to write the same two sentences with no rule
 *   about which the website believes — and the pair nobody remembered to edit
 *   is the one a search result would keep showing. What replaced them is the
 *   excerpt field saying out loud that it is also the search sentence, and
 *   counting against the width one gets.
 *
 * The cover image landed with [Web 14]/[Web 15], and "feature at the top of the
 * blog" with [Blog 6] — both are on the screen now.
 *
 * Two deliberate departures where something *is* storable:
 *
 * - **Author is a menu, and the names come from `users`.** The design lists
 *   Duy / Mai / Kenji as three fixed options, which stop being true the moment
 *   somebody else writes a post; the studio's own team does not ([Blog 9]). A
 *   new post is signed by whoever created it, stamped on the server from the
 *   session rather than defaulted in the browser, so a post written and never
 *   opened here still has a byline.
 * - **Reading time is gone entirely** ([Blog 10]). It had a field here that the
 *   design never drew, because the column existed and the website printed it —
 *   a public number nobody could correct is worse than none. Asked which of
 *   three ways to keep it true, the studio chose none: the column, the field
 *   and "6 min read" are all removed.
 */

/** The half of a post that is the same in every language, as the form holds it. */
interface Draft {
  slug: string;
  topicId: string;
  /** The account, or '' for a post nobody has linked to one. */
  authorId: string;
  /** The byline as the post says it — shown, never sent. */
  author: string;
  featured: boolean;
  /** `yyyy-mm-dd`, or empty for a draft — what `<input type="date">` speaks. */
  publishedOn: string;
}

/** One language of it. */
interface ContentDraft {
  title: string;
  excerpt: string;
  body: string;
  /**
   * What the cover shows, in the language being edited.
   *
   * In the draft with the words rather than beside the picture, because that
   * is what it is: the image is one picture for every language, and this is a
   * sentence somebody writes in each. It saves with the title and the body,
   * so writing Japanese cannot touch the English.
   */
  coverAlt: string;
  /** What a search result and a share say instead of the title and the excerpt; blank = automatic. */
  seoTitle: string;
  seoDescription: string;
}

/**
 * The publication instant as a date input can hold it, in UTC.
 *
 * `toISOString().slice(0, 10)` and not `getFullYear()` and friends: the local
 * calendar day is a different day west of Greenwich, so a post published at
 * midnight UTC would show in the editor as the day before and be saved back as
 * that day — moving every post one day earlier for anybody in the Americas.
 */
const dateInputValue = (published: string | null): string =>
  published ? new Date(published).toISOString().slice(0, 10) : '';

const draftOf = (post: AdminPost): Draft => ({
  slug: post.slug,
  topicId: post.topic?.id ?? '',
  authorId: post.authorId ?? '',
  author: post.author,
  featured: post.featured,
  publishedOn: dateInputValue(post.publishedAt),
});

const contentDraftOf = (post: AdminPost, locale: Locale): ContentDraft => ({
  title: post.title[locale] ?? '',
  excerpt: post.excerpt[locale] ?? '',
  body: post.body[locale] ?? '',
  coverAlt: post.coverAlt[locale] ?? '',
  seoTitle: post.seoTitle?.[locale] ?? '',
  seoDescription: post.seoDescription?.[locale] ?? '',
});

/**
 * The instant a chosen calendar day means.
 *
 * A date input gives a day with no time, and the column holds a moment, so
 * something here has to pick one. Two rules, and both were found by using the
 * screen rather than by reading it:
 *
 * - **Today means now.** Noon UTC was the first answer — the middle of the day
 *   everywhere, safely away from the midnight boundary. But a writer who picks
 *   today and presses Save at nine in the morning is publishing *now*, and noon
 *   turned that into a post scheduled for six hours' time, wearing a
 *   "Scheduled" badge nobody asked for.
 * - **Any other day means noon.** Midnight is the boundary itself: a day saved
 *   from a timezone ahead of UTC lands on a moment that has not arrived, and a
 *   post dated last Tuesday reads as scheduled. Noon is the middle of the day
 *   in every zone.
 */
const instantFrom = (day: string, now = new Date()): string | null => {
  if (!day) return null;
  return day === now.toISOString().slice(0, 10)
    ? now.toISOString()
    : new Date(`${day}T12:00:00.000Z`).toISOString();
};

/**
 * The side panel, as the mutation wants it.
 *
 * `featured` is the one field sent conditionally, because it is the one field
 * whose save changes a *different* post. Everything else here overwrites the
 * post somebody is looking at; featuring overwrites whichever post currently
 * holds the top of the blog. So a tab left open on a post that was featured an
 * hour ago, saved now for a typo, would put itself back at the top and take
 * down whatever somebody else has since chosen — with nothing on either screen
 * saying so. Sending it only when it changed makes a stale save mean what
 * every other stale save here means: it overwrites what it is looking at.
 */
const inputFrom = (draft: Draft, saved: AdminPost, touchedAuthor: boolean): UpdatePostInput => ({
  slug: draft.slug.trim(),
  topicId: draft.topicId || null,
  /*
   * The byline, only when somebody *chose* one — `touchedAuthor`, not a
   * comparison against what is stored.
   *
   * A post by "Duy Nguyen" came back from a save signed by whoever was logged
   * in, with nobody having touched the field. The first attempt at a guard here
   * compared `draft.author` with the saved value and claimed to protect against
   * "whatever the form happens to be holding" — which was wrong, and Em Liên
   * caught it: if the form is holding a wrong value then it *differs* from the
   * stored one, the comparison reads that as an edit, and the wrong value goes
   * out. It only ever suppressed sending a value identical to the server's,
   * which was already a no-op.
   *
   * A flag set from the select's own `onChange` is the thing that tells "a
   * person picked this" apart from "something set this" — and only the first
   * has any business rewriting a byline. React, for its part, does not fire
   * `onChange` when it renders a `<select>` whose value matches no option: it
   * quietly selects the first one and leaves state alone, so the screen can
   * disagree with the draft without a single event.
   */
  ...(touchedAuthor && draft.authorId !== '' ? { authorId: draft.authorId } : {}),
  ...(draft.featured === saved.featured ? {} : { featured: draft.featured }),
  publishedAt: instantFrom(draft.publishedOn),
});

const contentInputFrom = (content: ContentDraft): PostContentInput => ({
  title: content.title,
  excerpt: content.excerpt,
  body: content.body,
  coverAlt: content.coverAlt,
  seoTitle: content.seoTitle.trim(),
  seoDescription: content.seoDescription.trim(),
});

/**
 * What the toolbar writes into the body.
 *
 * Markdown, because that is what the column holds and what every other piece of
 * prose in this project is. The design's "Visual / HTML source" pair is gone
 * with it: a visual editor produces HTML, and putting raw HTML from a text box
 * onto a public page is a script-injection question, not a text box.
 */
const BLOCKS = [
  { label: 'Paragraph', prefix: '' },
  { label: 'Heading 2', prefix: '## ' },
  { label: 'Heading 3', prefix: '### ' },
  { label: 'Quote', prefix: '> ' },
  { label: 'Code block', prefix: '```\n', suffix: '\n```' },
] as const;

const Card = ({
  title,
  aside,
  children,
  gap = 12,
}: {
  title: string;
  /** A word or two beside the heading — a count, a format. */
  aside?: React.ReactNode;
  children: React.ReactNode;
  gap?: number;
}) => (
  <div className="panel flex flex-col px-4 py-3.5" style={{ gap }}>
    <span className="flex flex-wrap items-baseline gap-2.5">
      <h2 className="eyebrow m-0">{title}</h2>
      {aside}
    </span>
    {children}
  </div>
);

/**
 * Where a search result stops showing an excerpt.
 *
 * Roughly, and roughly is all it can be: Google measures pixels, not
 * characters, and the number moves. It is a mark on the counter rather than a
 * rule anything enforces — see the note on the counter itself.
 */
const SEARCH_LIMIT = 155;

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="field-label">{children}</span>
);

export default function PostEditorPage() {
  const { id = '' } = useParams();
  const query = useAdminPost(id);
  const topics = useAdminTopics();
  const team = useTeamMembers();
  /*
   * Whether a person has picked a byline on this screen, as opposed to the form
   * having ended up holding one. See `inputFrom`: this is what decides whether
   * the save says anything about the author at all.
   */
  const [touchedAuthor, setTouchedAuthor] = useState(false);
  const post = query.data ?? null;
  const update = useSavePost(id);
  /*
   * Outside the Save button, like an app's screenshots. The bytes go up in a
   * request of their own and the row points at what comes back, so holding the
   * change in the draft until Save would leave stored bytes nothing references
   * every time a save failed or a tab closed.
   */
  const cover = usePostCover(id);
  const shareImage = usePostShareImage(id);
  const website = useWebsiteUrl();
  const { publish } = useEditorBar();

  const [lang, setLang] = useState<Locale>('en');
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  /*
   * Null until the post arrives, then its values — not re-seeded on every
   * render. Seeding from `post` unconditionally throws away what somebody has
   * typed each time the query refetches, on window focus say, with no warning
   * and no undo.
   */
  const [draft, setDraft] = useState<Draft | null>(null);
  const saved = useMemo(() => (post ? draftOf(post) : null), [post]);
  /*
   * ⚠️ A save reloads the form whole; every other cache write is merged field
   * by field. See the app editor for the long version: `inputFrom` trims, so a
   * box tidied on the way out comes back as "no change" and a per-field seed
   * leaves the untidy version on screen with the bar stuck on "unsaved".
   *
   * The `coverAlt` effect below is this same bug, found first and patched where
   * it showed. It stays: clearing alt text a cover no longer has is right on
   * its own terms, whoever emptied the box.
   */
  const [savedAt, setSavedAt] = useState(0);

  useSeeded(saved, setDraft, String(savedAt));

  /*
   * The per-language half, keyed by the open tab. Switching tabs re-seeds it, so
   * one language is edited, saved and left — editing two at once would need a
   * draft per locale and a Save that writes several.
   */
  const [content, setContent] = useState<ContentDraft | null>(null);
  const savedContent = useMemo(() => (post ? contentDraftOf(post, lang) : null), [post, lang]);
  useSeeded(savedContent, setContent, `${lang}|${savedAt}`);

  /*
   * A description cannot outlive the picture it describes — including one
   * nobody had saved yet.
   *
   * Removing a cover clears the alt text on the server, but a language whose
   * alt was never saved has nothing to clear: the saved value was already
   * empty, so field-by-field seeding sees no change and leaves what was typed
   * sitting in a box that is now disabled. Save cannot shift it either, because
   * the server ignores alt text for a post with no cover — the form stays
   * "Unsaved" for good, over a word nobody can reach.
   */
  useEffect(() => {
    if (post?.coverUrl) return;
    setContent((current) => (current && current.coverAlt ? { ...current, coverAlt: '' } : current));
  }, [post?.coverUrl]);

  const dirty = useMemo(() => {
    if (!draft || !saved || !content || !savedContent) return false;
    return (
      JSON.stringify(draft) !== JSON.stringify(saved) ||
      JSON.stringify(content) !== JSON.stringify(savedContent)
    );
  }, [draft, saved, content, savedContent]);

  const failure = update.isError ? saveMessage(update.error) : null;

  /*
   * `update.mutate` and not `update`. `useMutation` hands back a fresh object
   * every render, so depending on the whole thing here publishes a new save bar
   * on every render, which sets state, which renders again — a loop that hangs
   * the test run rather than failing it. The app editor documents the same trap.
   */
  const mutate = update.mutate;
  const save = useCallback(() => {
    if (!draft || !content || !post) return;
    mutate(
      {
        shared: inputFrom(draft, post, touchedAuthor),
        locale: lang,
        content: contentInputFrom(content),
      },
      { onSuccess: () => setSavedAt((count) => count + 1) },
    );
  }, [mutate, draft, content, lang, post, touchedAuthor]);

  useEffect(() => {
    if (!post) {
      publish(null);
      return;
    }
    publish({ dirty, saving: update.isPending, error: failure, save });
  }, [post, dirty, update.isPending, failure, save, publish]);

  // Leaving must not leave the save bar behind on the next page.
  useEffect(() => () => publish(null), [publish]);

  const edit = (patch: Partial<Draft>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));
  const editContent = (patch: Partial<ContentDraft>) =>
    setContent((current) => (current ? { ...current, ...patch } : current));

  /**
   * Puts a block marker on the line the cursor is in.
   *
   * Applied to whole lines rather than to the selection, because that is what
   * these markers mean: `## ` is a property of a line, and wrapping half a
   * sentence in it produces a heading that starts mid-paragraph.
   */
  const applyBlock = (label: string) => {
    const block = BLOCKS.find((one) => one.label === label);
    const field = bodyRef.current;
    if (!block || !field || !content) return;

    const value = content.body;
    const lineStart = value.lastIndexOf('\n', Math.max(field.selectionStart - 1, 0)) + 1;
    const lineEnd = value.indexOf('\n', field.selectionStart);
    const end = lineEnd === -1 ? value.length : lineEnd;
    // Whatever marker is already there comes off first, so choosing Heading 3
    // after Heading 2 replaces it instead of stacking `### ## `.
    const line = value.slice(lineStart, end).replace(/^(#{1,6} |> )/, '');
    const marked = `${block.prefix}${line}${'suffix' in block ? block.suffix : ''}`;

    editContent({ body: `${value.slice(0, lineStart)}${marked}${value.slice(end)}` });
    field.focus();
  };

  if (query.isPending) {
    return (
      <section className="px-7 pt-7">
        <p className="text-[13.5px]" style={{ color: 'var(--muted)' }}>
          Loading…
        </p>
      </section>
    );
  }

  /*
   * A bookmark that has gone stale, or an id somebody typed. Back to the list
   * rather than an empty editor, which would look like a post with no content
   * and invite somebody to start filling it in.
   */
  if (!post) return <Navigate to="/blog" replace />;

  /*
   * The post has arrived but the drafts are seeded in an effect, so there is
   * exactly one render in between where they are still null. Sending *that*
   * render to the list would bounce somebody out of an editor that was about to
   * open — the first version of this line did, and every test below it landed
   * on the blog list instead.
   */
  if (!draft || !content) {
    return (
      <section className="px-7 pt-7">
        <p className="text-[13.5px]" style={{ color: 'var(--muted)' }}>
          Loading…
        </p>
      </section>
    );
  }

  const status = statusOf(post.publishedAt);
  const tabs = languageTabs(post.locales);
  const published = post.firstPublishedAt !== null;

  /*
   * What state the post is in, said once. It was a badge and a slug beside a
   * repeated <h1>; the header above now carries the title and the address, so
   * all that is left to say here is the part the header cannot know.
   */
  const editorMeta = [
    status.toLowerCase(),
    post.author.trim() ? `by ${post.author.trim()}` : null,
    `edited ${postDate(post.updatedAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <EditorTabs
        back="/blog"
        backLabel="Back to the blog"
        tabs={tabs}
        current={lang}
        onPick={setLang}
        meta={editorMeta}
        label="Post language"
        translate={{ kind: 'post', id }}
      />

      <section className="flex flex-wrap items-start gap-3 px-[18px] pt-3.5 pb-6">
        <div className="flex min-w-0 flex-[1_1_440px] flex-col gap-3">
          <Card title="Post">
            {/*
              The title is the page's own heading in v2 — a borderless field on
              an underline rather than a box, so the editor opens on the words
              rather than on a form.
            */}
            <label className="flex flex-col gap-[7px]">
              <span className="sr-only">Title</span>
              <input
                value={content.title}
                placeholder="Post title"
                onChange={(event) => editContent({ title: event.target.value })}
                className="focus-ring w-full min-h-[30px] border-0 border-b bg-transparent px-0 pt-0 pb-2 text-[17px] font-semibold tracking-[-0.02em]"
                style={{ borderColor: 'var(--line)', color: 'var(--text)' }}
              />
            </label>
            <label className="flex flex-col gap-[7px]">
              <Label>Slug</Label>
              <input
                value={draft.slug}
                placeholder="post-url-slug"
                disabled={published}
                onChange={(event) => edit({ slug: event.target.value })}
                className="field field-mono disabled:opacity-60"
              />
              {/*
                Locked rather than absent once the post has been public. The
                field is where somebody looks for the address, and the sentence
                below it is the answer to "why can I not edit this?" — the
                server refuses the same change, in the same words.
              */}
              {published ? (
                <span className="text-xs leading-[1.55]" style={{ color: 'var(--muted)' }}>
                  This post has been published, so its address cannot change — every link anybody
                  already has would stop working.
                </span>
              ) : null}
            </label>
            <label className="flex flex-col gap-[7px]">
              {/*
                Both of its jobs, named. It said "blog index" alone, which is
                half true: this is also the post's `description` and
                `og:description`, so it is the sentence Google and every shared
                link print. [Blog 5] settled that `excerpt` keeps both jobs —
                and a field doing two jobs has to say so, or the person writing
                it is only told about one.
              */}
              <Label>Excerpt · blog index and search results</Label>
              <textarea
                rows={2}
                value={content.excerpt}
                placeholder="One or two sentences."
                onChange={(event) => editContent({ excerpt: event.target.value })}
                className="field resize-y leading-[1.55]"
              />
              <span
                className="flex items-center gap-1.5 text-[11px] leading-[1.5]"
                style={{
                  color: content.excerpt.length > SEARCH_LIMIT ? 'var(--warn)' : 'var(--dim)',
                }}
              >
                {/*
                  ⚠️ A mark, not a limit, and `maxLength` was the wrong reflex.
                  Truncating is Google's decision and it is not destructive — the
                  sentence is still whole on the blog index, which is this
                  field's other job and has more room. Refusing the 156th
                  character here would be this screen enforcing somebody else's
                  display width on the studio's own writing.
                */}
                <span className="font-mono tabular-nums">{content.excerpt.length}</span>
                <span>
                  / {SEARCH_LIMIT} before a search result cuts it — the blog index shows it whole
                </span>
              </span>
            </label>
          </Card>

          <div className="panel px-4 py-3.5">
            <MarkdownField
              label="Body"
              heading
              value={content.body}
              onChange={(body) => editContent({ body })}
              placeholder="Start writing. ## for a heading, - for a list, > for a quote."
              hint="Markdown: ## heading, **bold**, *italic*, - list, > quote, [text](url), `code`."
            />
          </div>

          {/*
            Cover and search appearance. The cover and its alt text save; the
            two meta fields still do not, and that is [Blog 5] — the card that
            has to decide whether they are columns at all or whether the
            excerpt keeps playing that part on the website.
          */}
          <Card
            title="Cover & search appearance"
            aside={
              <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
                {post.coverUrl ? 'cover set' : 'no cover'}
              </span>
            }
          >
            <div className="flex flex-wrap items-start gap-3">
              {/*
                The label is the box: a `<label>` around a hidden file input is
                what makes the whole rectangle a target, and the name lives in
                the `sr-only` span because the rectangle itself is a picture or
                a size.
              */}
              <label
                className="focus-ring flex aspect-[16/9] w-[140px] flex-none cursor-pointer items-center justify-center overflow-hidden rounded-[9px] border font-mono text-[10px]"
                style={{
                  borderColor: 'var(--line2)',
                  borderStyle: post.coverUrl ? 'solid' : 'dashed',
                  background: 'var(--in)',
                  color: 'var(--dim)',
                }}
              >
                <span className="sr-only">
                  {post.coverUrl ? 'Replace the cover image' : 'Add a cover image'}
                </span>
                {post.coverUrl ? (
                  <img
                    src={post.coverUrl}
                    alt="The cover image"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span aria-hidden="true">{cover.isPending ? '…' : '1600 × 900'}</span>
                )}
                <input
                  type="file"
                  className="sr-only"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={cover.isPending}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    /*
                     * Cleared straight away, so choosing the same file twice in
                     * a row still fires a change — the ordinary path after an
                     * upload is refused and the same file is exported again.
                     *
                     * Knowingly unguarded: jsdom will not let a file input's
                     * value be set to anything but the empty string, so no test
                     * can put it in the state this line clears.
                     */
                    event.target.value = '';
                    /*
                     * And only when there is one. A picker closed with Cancel
                     * fires a change with no file, and `mutate(undefined)`
                     * sends `fileId: null` — which is the mutation for *remove
                     * the cover*. Cancelling would delete the picture.
                     */
                    if (file) cover.mutate(file);
                  }}
                />
              </label>
              <span className="flex min-w-0 flex-[1_1_220px] flex-col gap-2">
                <label className="flex flex-col gap-1.5">
                  <Label>Alt text · {localeLabels[lang]}</Label>
                  {/*
                    Off until there is a picture. A description with no image is
                    a sentence that will be read aloud about the *next* one,
                    whatever that turns out to be — which is why removing a
                    cover clears the words, and why they cannot be written
                    before one exists either.
                  */}
                  <input
                    value={content.coverAlt}
                    disabled={!post.coverUrl}
                    placeholder={
                      post.coverUrl ? 'Describe the image for screen readers' : 'Add a cover first'
                    }
                    onChange={(event) => editContent({ coverAlt: event.target.value })}
                    className="field disabled:opacity-50"
                  />
                </label>
                {/*
                  ⚠️ **Meta title and meta description used to sit here**, two
                  disabled boxes waiting on [Blog 5]. That card took them off:
                  `title` and `excerpt` are what the website prints, and two
                  boxes for the same two sentences is how the two drift.

                  They are back at the foot of the page as overrides — blank by
                  default, showing what the page says on its own — which is the
                  form in which a drift is a decision rather than an accident.
                */}
              </span>
            </div>
            {cover.isError ? (
              <span
                className="text-[11px] leading-[1.5]"
                role="alert"
                style={{ color: 'var(--danger)' }}
              >
                {saveMessage(cover.error)}
              </span>
            ) : (
              <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
                PNG, JPEG or WebP. 1600 × 900 is what the list cards crop to; the post page shows
                the picture whole, whatever its shape. Any size is accepted. The alt text is per
                language — this one is {localeLabels[lang]} — and it saves with the rest of the
                language. The title and excerpt above are what a search result and a shared link
                show, unless Search &amp; sharing at the foot of the page says otherwise.
              </span>
            )}
            {post.coverUrl ? (
              <button
                type="button"
                disabled={cover.isPending}
                onClick={() => cover.mutate(null)}
                className="btn-quiet focus-ring w-fit cursor-pointer text-[11.5px] disabled:cursor-default disabled:opacity-40"
                style={{ color: 'var(--dim)' }}
              >
                Remove
              </button>
            ) : null}
          </Card>

          {/*
            Last in the left column: what a search result and a shared link show
            for this post, in the language open above, with the boxes to say
            something else. The title and the excerpt above stay what the page
            prints — see the note on the cover card — and these are blank until
            somebody decides a result should read differently.
          */}
          <SearchSharingCard
            lang={lang}
            url={`${website}/${lang}/blog/${post.slug}`}
            auto={{
              title: content.title.trim() || post.title.en || '',
              description: content.excerpt.trim() || post.excerpt.en || '',
            }}
            value={{ title: content.seoTitle, description: content.seoDescription }}
            onChange={(patch) =>
              editContent({
                ...(patch.title !== undefined ? { seoTitle: patch.title } : {}),
                ...(patch.description !== undefined ? { seoDescription: patch.description } : {}),
              })
            }
            image={{
              current: post.shareImageUrl,
              // The website draws nothing for a draft or a scheduled post: a 404 until live.
              generated:
                status === 'Published'
                  ? previewUrl(website, `/${lang}/blog/${post.slug}`, post.updatedAt)
                  : null,
              pending: shareImage.isPending,
              error: shareImage.isError ? saveMessage(shareImage.error) : null,
              onChange: (file) => shareImage.mutate(file),
            }}
          />
        </div>

        <div className="flex min-w-0 flex-[0_1_340px] flex-col gap-3">
          <Card title="Publishing" gap={14}>
            {/*
              ⚠️ The design's Status menu is not here, and this is the one place
              the screen argues with it. The date already says which of the
              three states a post is in — empty is a draft, a past day is
              published, a day ahead is scheduled — so a menu beside it is a
              second control for one column, free to disagree with it. Raised
              for a decision rather than built both ways.
            */}
            <label className="flex flex-col gap-1.5">
              <Label>Publish date</Label>
              {/*
                One date input and no Status menu, though the design draws both.
                The date already says which of the three states this is — empty
                is a draft, a past day is published, a future one is scheduled —
                and a menu beside it would be a second control for the same
                column, able to disagree with it.
              */}
              <input
                type="date"
                aria-label="Publish date"
                value={draft.publishedOn}
                onChange={(event) => edit({ publishedOn: event.target.value })}
                className="field"
              />
              <span className="text-xs leading-[1.5]" style={{ color: 'var(--muted)' }}>
                {draft.publishedOn === ''
                  ? 'No date: this stays a draft.'
                  : status === 'Scheduled'
                    ? 'A date ahead of today: it goes live on its own that morning.'
                    : 'Live on the blog.'}
              </span>
            </label>
            <label className="flex flex-col gap-1.5">
              <Label>Author</Label>
              {/*
                A list, not a box. Typed by hand, "Duy Nguyen" and "Duy" became
                two authors on the website with nothing to say they were one
                person.

                Accounts, by id, so the name and the photo follow the person.
                What it offers is the team plus, when the post says a name
                and points at nobody — the posts the design importer wrote —
                that name, kept and marked; a list of the team alone would
                leave each of them selecting nothing, and the first save would
                re-sign a published post to whoever sorted first.
              */}
              <select
                value={draft.authorId}
                onChange={(event) => {
                  setTouchedAuthor(true);
                  edit({ authorId: event.target.value });
                }}
                className="field"
              >
                {draft.authorId === '' && draft.author.trim() === '' ? (
                  <option value="">Who wrote it</option>
                ) : null}
                {bylineOptions(team.data ?? [], {
                  authorId: draft.authorId || null,
                  author: draft.author,
                }).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </Card>

          <Card title="Topic" gap={14}>
            <select
              aria-label="Topic"
              value={draft.topicId}
              onChange={(event) => edit({ topicId: event.target.value })}
              className="field"
            >
              {/*
                A real option, not a placeholder. A draft can exist before
                anybody has decided what it is about, and the column is nullable
                for that reason.
              */}
              <option value="">No topic yet</option>
              {(topics.data ?? []).map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topicName(topic.name)}
                </option>
              ))}
            </select>
            <span className="text-xs leading-[1.55]" style={{ color: 'var(--muted)' }}>
              Topics are added and renamed from the Topics dialog on the blog list.
            </span>
          </Card>

          <Card title="On the blog">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.featured}
                onChange={(event) => edit({ featured: event.target.checked })}
                className="h-[15px] w-[15px]"
              />
              <span className="text-[12.5px]" style={{ color: 'var(--muted)' }}>
                Feature at the top of the blog
              </span>
            </label>
            {/*
              Said here, because the box on its own does not say it. One post is
              featured at a time — ticking this one takes the last one down — and
              a draft can be ticked and simply waits, which is why the sentence
              names publication rather than leaving somebody to test it.
            */}
            <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
              {draft.featured !== post.featured
                ? /*
                   * The tick has moved and nothing has been saved, so neither
                   * of the sentences below is true yet. Read from `post` and
                   * not `draft`, which is what made this say "This is the post
                   * at the top of the blog" the instant somebody ticked it.
                   */
                  draft.featured
                  ? 'It becomes the featured post when you save.'
                  : 'It stops being the featured post when you save.'
                : draft.featured
                  ? /*
                     * And the publication state read from the draft too: a
                     * featured post whose date has just been cleared is about
                     * to be a draft, and saying it is at the top of the blog
                     * is the same lie one save later.
                     */
                    statusOf(instantFrom(draft.publishedOn)) === 'Published'
                    ? /*
                       * "Is the featured post", not "is at the top of the
                       * blog". The website chooses its own front-page post from
                       * a static file and does not read this yet — that is
                       * [Web 16] — so the second sentence would be false today
                       * on a screen that has no way of knowing it. What is true
                       * either way is what this setting says.
                       */
                      'This is the featured post. Featuring another one takes it down.'
                    : 'It becomes the featured post once this post is published.'
                  : 'One post at a time. Featuring this one takes the current one down.'}
            </span>
          </Card>
        </div>
      </section>
    </>
  );
}
